import type { GatewayCreditselectionService } from 'commonTypes/api/gatewayCreditselectionService';

export type ScenarioNavigation = {
  step: SCENARIO_STEP;
  type: 'internal' | 'external';
};

export enum SummaryLabel {
  LoanAmountAgreement = 'LoanAmountAgreement',
  LoanAmount = 'LoanAmount',
  LoanPerMonth = 'LoanPerMonth',
  LoanTerm = 'LoanTerm',
  LoanRate = 'LoanRate',
  LoanRatePsk = 'LoanRatePsk',
  LoanOverpayment = 'LoanOverpayment',
  LoanInsuranceRate = 'LoanInsuranceRate',
  LoanInsuranceAmount = 'LoanInsuranceAmount',
  LoanPsk = 'LoanPsk',
  LoanPayOffBefore = 'LoanPayOffBefore',
  LoanLimit = 'LoanLimit',
  LoanGracePeriod = 'LoanGracePeriod',
  LoanServiceCost = 'LoanServiceCost',
  LoanInstallmentPeriod = 'LoanInstallmentPeriod',
  LoanTermFromTo = 'LoanTermFromTo',
  LoanPerMonthExact = 'LoanPerMonthExact',
  LoanRateRange = 'LoanRateRange',
  LoanInsurance = 'LoanInsurance',
  InitialPaymentSize = 'InitialPaymentSize',
  LoanRepaymentAmount = 'LoanRepaymentAmount',
  LoanRegularPayment = 'LoanRegularPayment',
  LoanOpeningDate = 'LoanOpeningDate',
  LoanPerMonthWithTerm = 'LoanPerMonthWithTerm',
  Loading = 'Loading',
  LoanRatePskExact = 'LoanRatePskExact',
}

export enum EXTERNAL_SCENARIO_STEP {
  NEXT = 'NEXT',
}

export type Color = 'green' | 'orange' | 'blue' | 'red' | 'dark';

export type Badge = {
  type: 'primary' | 'secondary';
  title: string;
  color: Color;
  icon?: string;
  code: 'creditCard' | 'preApprovalOffer' | 'credit';
};

export type ModifyBadgesResult = Array<{
  text: string | JSX.Element;
  variant: 'primary' | 'secondary';
  color: Color;
  tooltipContent?: string;
  tooltipIcon?: 'tip';
}>;

export type Summary = {
  value: string;
  valueRaw:
    | number
    | {
        from: number;
        to: number;
      };
  label: string;
  text: string;
  tooltip: string;
};

export interface StatusStep {
  id: string;
  title: string;
  prev?: string;
  subtitle?: string;
  ordering: number;
  isActive: boolean;
}

export interface StatusBlock {
  title: string;
  stepProgress: StatusStep[];
}

export type ButtonType = 'primary' | 'secondary' | 'text' | 'outlined';

export type TButtonFlow<InteractionClick = never> = {
  label?: string;
  id?: string;
  title?: string;
  onClick: TOnClickFlow | InteractionClick;
  type?: ButtonType;
  disabled?: boolean;
  color?: Color;
};

export type TPayload = {
  amount?: number;
  term?: string;
  agreementId?: string;
  organizationId?: number;
  documentType?: string;
  agreementConfirmCode?: string;
  sendAgreementConfirmCode?: boolean;
  ksid?: string;
  client?: {
    ip?: string;
    userAgent?: string;
    deviceModel?: string;
    port?: number;
  } & Record<string, unknown>;
  analytics?: Record<string, unknown>;
  preApprovalRequestParams?: GatewayCreditselectionService.PreApprovalsSearchClientRequest;
} & Record<string, any>;

export type TAction = {
  step: SCENARIO_STEP;
  payload?: TPayload;
  redirectUrl?: string;
};

export type TOnClickFlow = {
  type: string;
  action: TAction;
};

export type OfferInfo = {
  amount: { value: string; valueRaw: number };
  organizationId: number;
  organizationName: string;
  bankOfferId: string;
  organizationLogo: string;
  alias: string;
  productType: string;
  summaries: Summary[];
  onClick: TOnClickFlow;
  buttons?: TButtonFlow[];
  creditCardLogo: string;
};

export type TSummaryValueRaw = {
  from: string | number;
  to: string | number;
};

export type TSummaryValue = {
  from: string;
  to: string;
};

export interface Header {
  title: string;
  onClick: {
    type: string;
    action: {
      step: SCENARIO_STEP;
    };
  };
}

/**
 * Базовый интерфейс для сценариев, который содержит общие поля
 * Step - шаг сценария
 * Content - контент
 * Interactions - если не передали тип для interaction, то interaction не будет в подсказках
 * Rules - если не передали тип для rules, то interaction не будет в подсказках
 */
export type BaseScenario<Step extends SCENARIO_STEP, Content, Interactions = never, Rules = never> = {
  analytics?: {
    flow?: {
      category?: number;
      subCategory?: number;
    };
    calcId?: string;
  };
  step: Step;
  flowId: string;
  type: 'internal' | 'external';
  redirectUrl?: string;
  content: Content;
  nextStep?: ScenarioNavigation;
  prevStep?: ScenarioNavigation;
} & (Interactions extends never ? {} : { interactions: Interactions }) &
  (Rules extends never ? {} : { rules: Rules });

export enum SCENARIO_STEP {
  CARD_DELIVERY_INVITE = 'CARD_DELIVERY_INVITE',
  ERROR_STEP = 'ERROR_STEP',
  CREDIT_SELECTION = 'CREDIT_SELECTION',
  CREDIT_OFFERS_LOADING = 'CREDIT_OFFERS_LOADING',
  GO_TO_LEADGEN = 'GO_TO_LEADGEN',
  OFFER_DISBURSEMENT = 'OFFER_DISBURSEMENT',
  CONDITIONS_SELECTION = 'CONDITIONS_SELECTION',
  DOCUMENTS_LOADING = 'DOCUMENTS_LOADING',
  OFFER_APPROVAL = 'OFFER_APPROVAL',
  AGREEMENT_SIGNING = 'AGREEMENT_SIGNING',
  OFFER_COOLING_PERIOD = 'OFFER_COOLING_PERIOD',
  FRAUD_RISK_DETECTED = 'FRAUD_RISK_DETECTED',
  FRAUD_PREVENTION = 'FRAUD_PREVENTION',
  AGREEMENT_SIGNED = 'AGREEMENT_SIGNED',
  CLIENT_REFUSED = 'CLIENT_REFUSED',
  CREDIT_SELF_RESTRICTION = 'CREDIT_SELF_RESTRICTION',
}

export interface INextScenarioRequest {
  flow: string;
  step?: SCENARIO_STEP;
  owner: number;
  platform?: string;
  payload?: TPayload & {
    analytics?: Record<string, unknown>;
    preApprovalRequestParams?: GatewayCreditselectionService.PreApprovalsSearchClientRequest;
  };
}

export interface IStartScenarioRequest {
  scenarioName: string;
  owner: number;
  platform: string;
}

export interface ISendStepScenarioRequest {
  step: SCENARIO_STEP;
}

export interface IStartScenarioResponse {
  item: string;
}
